import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Phone, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/r`;

export default function Respond() {
  const { code } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({ name: "", phone: "", best_time: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { axios.get(`${API}/${code}`).then((r) => setD(r.data)).catch(() => setErr("We couldn't find that response code. Please check the code on your postcard.")); }, [code]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { await axios.post(`${API}/${code}/callback`, form); setDone(true); }
    catch (x) { setErr(x.response?.data?.detail || "Something went wrong — please call us instead."); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#37494d] px-4 py-10 text-white" data-testid="respond-page">
      <div className="mx-auto max-w-md">
        <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#c9a227]">Elite Real Estate &amp; Finance</div>
        {!d && !err && <Loader2 className="mt-10 h-6 w-6 animate-spin" />}
        {err && !d && <p className="mt-8 text-sm text-red-200" data-testid="respond-error">{err}</p>}
        {d && (
          <>
            <h1 className="mt-4 font-display text-3xl font-extrabold">Thanks for reaching out{d.first_name ? `, ${d.first_name}` : ""}.</h1>
            <p className="mt-2 text-sm text-slate-200">Your loan officer will walk you through your {d.opportunity_type ? d.opportunity_type.toLowerCase() : "mortgage"} options — no obligation.</p>
            <div className="mt-6 rounded-xl bg-white p-5 text-slate-900" data-testid="respond-mlo-card">
              <div className="text-lg font-bold">{d.mlo.name}</div>
              {d.mlo.nmls && <div className="text-xs text-slate-500">NMLS #{d.mlo.nmls}</div>}
              <a href={`tel:${d.mlo.phone}`} className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#c9a227] px-4 py-2 text-sm font-bold text-slate-900" data-testid="respond-call-btn"><Phone className="h-4 w-4" /> Call {d.mlo.phone}</a>
              {done ? (
                <div className="mt-5 flex items-center gap-2 text-sm font-semibold text-emerald-700" data-testid="respond-success"><CheckCircle2 className="h-5 w-5" /> Got it — {d.mlo.name.split(" ")[0]} will call you soon.</div>
              ) : (
                <form onSubmit={submit} className="mt-5 space-y-2">
                  <div className="text-sm font-semibold">Or request a call back</div>
                  <Input placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="respond-name" />
                  <Input required type="tel" placeholder="Phone number" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="respond-phone" />
                  <Input placeholder="Best time to call (optional)" value={form.best_time} onChange={(e) => setForm({ ...form, best_time: e.target.value })} data-testid="respond-time" />
                  {err && <p className="text-xs text-red-600" data-testid="respond-form-error">{err}</p>}
                  <Button type="submit" disabled={busy} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="respond-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Request a call back"}</Button>
                </form>
              )}
            </div>
            <p className="mt-6 text-[10px] leading-relaxed text-slate-300">{d.mlo.company} · NMLS #{d.mlo.company_nmls} · Equal Housing Lender. This is not a commitment to lend; all loans are subject to credit approval and underwriting.</p>
          </>
        )}
      </div>
    </div>
  );
}
